import { Dispatch, SetStateAction } from 'react';
import Image from 'next/image';
import { clsx } from 'clsx';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/react';
import Link from 'next/link';

import { Icons } from '@monoimin/atoms';

import BurgerButton from '../../burger-button';

import { LocaleSwitcher } from './LocaleSwitcher';
import { HeaderNavigation } from './HeaderNavigation';

import type { HeaderCompactProps } from '../HeaderCompact';

type HeaderBodyProps = Omit<HeaderCompactProps, 'panelVariant'> & {
  menuOpen: boolean;
  setMenuOpen: Dispatch<SetStateAction<boolean>>;
};

export const HeaderBody = ({
  blok,
  logoSrc = '/images/logo.svg',
  logoAlt,
  localesList,
  classNames = {},
  menuOpen,
  setMenuOpen,
  logoLinkHref,
}: HeaderBodyProps) => {
  const { links, contactLinks } = blok;

  const withLocales = Array.isArray(localesList) && localesList.length > 0;

  const withContacts = Array.isArray(contactLinks) && contactLinks.length > 0;

  return (
    <div
      className={clsx(
        'header-body flex w-full items-center justify-between',
        classNames?.headerBody
      )}
    >
      <div
        className={clsx(
          'header-logo-container flex items-center gap-3',
          classNames?.logoContainer
        )}
      >
        <BurgerButton
          isOpen={menuOpen}
          onClick={() => {
            setMenuOpen((prev) => !prev);
          }}
          className='block min-[1200px]:hidden'
        />
        <Link
          href={logoLinkHref || '/'}
          className={clsx('header-logo-link relative', classNames?.logoLink)}
          aria-label='My Golden Visa: Home'
        >
          <Image
            alt={logoAlt || ''}
            fill
            quality='65'
            priority
            src={logoSrc}
            className={(clsx('h-auto object-contain'), classNames?.logoImage)}
          />
        </Link>
      </div>
      {Array.isArray(links) && links.length > 0 && (
        <HeaderNavigation
          links={links}
          linkClassName={classNames?.headerLink}
        />
      )}
      {(withLocales || withContacts) && (
        <div className='flex items-center gap-12'>
          {withLocales && (
            <LocaleSwitcher
              localesList={localesList}
              className='hidden min-[1200px]:block'
              optionsClassName='right-0 mt-2'
            />
          )}
          {withContacts && (
            <Menu
              as='div'
              className={clsx('listbox', classNames?.contactsContainer)}
            >
              <MenuButton className='button group flex items-center gap-1'>
                <Icons icon='messenger' />
                <Icons
                  icon='dropdown'
                  className='group-aria-expanded:rotate-180'
                  isInteractive
                />
              </MenuButton>
              <MenuItems as='ul' className='listbox__options right-0 mt-2'>
                {contactLinks.map((item) => {
                  const { link, _uid } = item;

                  return (
                    <MenuItem key={_uid} as='li' className='listbox__option'>
                      <Link
                        href={link?.url}
                        className='whitespace-nowrap'
                        target='_blank'
                      >
                        {link?.title}
                      </Link>
                    </MenuItem>
                  );
                })}
              </MenuItems>
            </Menu>
          )}
        </div>
      )}
    </div>
  );
};
